import { useState, useEffect, useCallback, useRef } from 'react';

/** How often (ms) to proactively check for a new service worker. */
const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000; // 60 minutes

/**
 * Hook that detects when a new service worker version is available.
 *
 * It listens for `updatefound` on the current registration and also
 * checks for a waiting worker that was installed before the hook mounted.
 *
 * Proactive checks are triggered when:
 * - The page regains visibility (e.g. user switches back to the PWA)
 * - A periodic interval elapses (every 60 minutes while active)
 *
 * Returns:
 * - `updateAvailable` – true when a new SW is installed and waiting
 * - `applyUpdate` – tells the waiting SW to activate, then reloads
 */
export function useServiceWorkerUpdate() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleInstalled = (worker: ServiceWorker) => {
      // Only flag an update if there is already a controlling SW –
      // i.e. this is not the very first install.
      if (navigator.serviceWorker.controller) {
        setWaitingWorker(worker);
        setUpdateAvailable(true);
      }
    };

    const trackInstalling = (worker: ServiceWorker | null) => {
      if (!worker) return;
      worker.addEventListener('statechange', () => {
        if (worker.state === 'installed') {
          handleInstalled(worker);
        }
      });
    };

    navigator.serviceWorker.ready.then((reg) => {
      registrationRef.current = reg;

      // A worker might already be waiting (e.g. page was not refreshed
      // after the previous deploy).
      if (reg.waiting) {
        handleInstalled(reg.waiting);
      }

      // A new worker could already be installing.
      if (reg.installing) {
        trackInstalling(reg.installing);
      }

      // Listen for future updates.
      reg.addEventListener('updatefound', () => {
        trackInstalling(reg.installing);
      });
    });

    // When the new SW takes over, reload so the page uses new assets.
    const onControllerChange = () => window.location.reload();
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
    };
  }, []);

  // --- Proactive update checks ---
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    /** Safely ask the browser to look for a newer service worker. */
    const checkForUpdate = () => {
      registrationRef.current?.update().catch(() => {
        // update() can throw if the SW script is unreachable (e.g. offline).
        // Swallow the error — the next check or visibility change will retry.
      });
    };

    // 1. Check when the user switches back to the app (critical for mobile PWAs
    //    where the app may sit in the background for days).
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkForUpdate();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    // 2. Periodic check while the app is active.
    const intervalId = setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL_MS);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      clearInterval(intervalId);
    };
  }, []);

  /**
   * Send `SKIP_WAITING` to the waiting worker so it activates.
   * The `controllerchange` listener above will then reload the page.
   */
  const applyUpdate = useCallback(() => {
    if (waitingWorker) {
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    }
  }, [waitingWorker]);

  return { updateAvailable, applyUpdate };
}
